import { useMemo } from "react";

import { deconstructLeaseTerms } from "@at/leasing-utils";

import { LeaseContractType } from "../../../../../__generated__/graphql-types";
import { useLeasingStateContext } from "../../../../../contexts/leasing/context/leasing.context";
import { getFilteredLeaseOffersByStock } from "../../../../../utils/leasing/getFilteredLeaseOffersByStock/get-filtered-lease-offers-by-stock";
import { getPrice } from "../../../../../utils/leasing/getPrice/get-price";

export const useLeasingData = () => {
  const state = useLeasingStateContext();
  const {
    selectedLeaseOffer,
    leaseContractType,
    leaseOption,
    leaseOffersData,
    selectedStockFilter,
  } = state;

  const leaseTermParts = deconstructLeaseTerms(leaseOption);

  const isBusinessContract = leaseContractType === LeaseContractType.business;

  const unformattedMonthlyPrice =
    selectedLeaseOffer?.[
      isBusinessContract ? "monthlyPriceExVat" : "monthlyPrice"
    ] ?? 0;

  const monthlyPrice = useMemo(
    () => getPrice(selectedLeaseOffer, leaseOption, unformattedMonthlyPrice),
    [selectedLeaseOffer, leaseOption, unformattedMonthlyPrice],
  );

  const deposit = useMemo(
    () =>
      getPrice(
        selectedLeaseOffer,
        leaseOption,
        leaseTermParts
          ? unformattedMonthlyPrice * leaseTermParts.depositMultiple
          : null,
      ),
    [leaseOption, leaseTermParts, selectedLeaseOffer, unformattedMonthlyPrice],
  );

  const availableLeaseOffers = useMemo(
    () =>
      getFilteredLeaseOffersByStock(leaseOffersData || [], selectedStockFilter),
    [leaseOffersData, selectedStockFilter],
  );

  const colourList = useMemo(() => {
    const uniqueColours = new Set<string>();
    for (const offer of availableLeaseOffers) {
      offer.colour && uniqueColours.add(offer.colour);
    }
    return [...uniqueColours];
  }, [availableLeaseOffers]);

  const trimList = useMemo(() => {
    const uniqueTrims = new Set<string>();
    const availableOffersForSelectedColour = availableLeaseOffers.filter(
      (offer) => offer.colour === selectedLeaseOffer?.colour,
    );
    for (const offer of availableOffersForSelectedColour) {
      offer.upholstery && uniqueTrims.add(offer.upholstery);
    }
    return [...uniqueTrims];
  }, [availableLeaseOffers, selectedLeaseOffer?.colour]);

  return {
    state,
    unformattedMonthlyPrice,
    leaseTermParts,
    monthlyPrice,
    deposit,
    isBusinessContract,
    availableLeaseOffers,
    colourList,
    trimList,
  };
};
